import { useState, useEffect } from 'react';
import { TREATMENTS, LOCAL_UNITS } from '../../mocks/treatments';
import type { LocalUnit } from '../../types';

interface AgendamentoModalProps {
  onClose: () => void;
}

export default function AgendamentoModal({ onClose }: AgendamentoModalProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [local, setLocal] = useState<LocalUnit>('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleAgendar = () => {
    const treatment = TREATMENTS.find((t) => t.id === selected);
    if (!treatment) return;

    const localText =
      local === 'criciumia' ? ' — Unidade *Criciúma (KASA DERMA)*' :
      local === 'tubarao'   ? ' — Unidade *Tubarão (Grupo Naiana SA)*' : '';

    const fullMessage = treatment.message.replace('😊', `${localText} 😊`);
    window.open(`https://wa.me/5548991002443?text=${encodeURIComponent(fullMessage)}`, '_blank');
    onClose();
  };

  const previewMessage = (() => {
    const t = TREATMENTS.find((tr) => tr.id === selected);
    if (!t) return '';
    const localText =
      local === 'criciumia' ? ' — Unidade Criciúma (KASA DERMA)' :
      local === 'tubarao'   ? ' — Unidade Tubarão (Grupo Naiana SA)' : '';
    return t.message.replace('😊', `${localText} 😊`);
  })();

  const toggleLocal = (id: 'criciumia' | 'tubarao') => {
    setLocal((prev) => (prev === id ? '' : id));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl px-6 pt-6 pb-4 border-b border-[#E8DCC8] z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-medium text-[#4A3F35]">Agendar pelo WhatsApp</h3>
              <p className="text-sm text-[#8B7355] mt-1">Selecione o tratamento desejado</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Fechar modal"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#FBF8F3] hover:bg-[#E8DCC8] transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-[#8B7355] text-xl" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {/* Tipo de Tratamento */}
          <p className="text-xs font-semibold text-[#A89580] uppercase tracking-widest mb-3">
            Tipo de Tratamento *
          </p>
          <div className="grid grid-cols-1 gap-2 mb-6">
            {TREATMENTS.map((t) => {
              const isSelected = selected === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelected(t.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all cursor-pointer ${
                    isSelected
                      ? 'border-[#C4A57B] bg-[#FBF8F3] shadow-sm'
                      : 'border-[#E8DCC8] bg-white hover:border-[#C4A57B]/50 hover:bg-[#FBF8F3]/50'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#C4A57B]' : 'bg-[#FBF8F3]'}`}>
                    <i className={`${t.icon} text-base ${isSelected ? 'text-white' : 'text-[#C4A57B]'}`} />
                  </div>
                  <span className={`text-sm font-medium ${isSelected ? 'text-[#4A3F35]' : 'text-[#6B5345]'}`}>
                    {t.label}
                  </span>
                  {isSelected && <i className="ri-check-line text-[#C4A57B] text-base ml-auto" />}
                </button>
              );
            })}
          </div>

          {/* Unidade */}
          <p className="text-xs font-semibold text-[#A89580] uppercase tracking-widest mb-3">
            Unidade de Atendimento (opcional)
          </p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {LOCAL_UNITS.map((l) => {
              const isActive = local === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => toggleLocal(l.id)}
                  className={`flex flex-col items-center gap-1 px-4 py-4 rounded-xl border text-center transition-all cursor-pointer ${
                    isActive
                      ? 'border-[#C4A57B] bg-[#FBF8F3] shadow-sm'
                      : 'border-[#E8DCC8] bg-white hover:border-[#C4A57B]/50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? 'bg-[#C4A57B]' : 'bg-[#FBF8F3]'}`}>
                    <i className={`${l.icon} text-sm ${isActive ? 'text-white' : 'text-[#C4A57B]'}`} />
                  </div>
                  <span className={`text-sm font-medium ${isActive ? 'text-[#4A3F35]' : 'text-[#6B5345]'}`}>
                    {l.label}
                  </span>
                  <span className="text-xs text-[#A89580]">{l.sub}</span>
                </button>
              );
            })}
          </div>

          {/* Prévia da mensagem */}
          {selected && (
            <div className="bg-[#E7F8EE] border border-[#C3E6CB] rounded-xl px-4 py-3 mb-6">
              <p className="text-xs font-semibold text-[#3A7D52] mb-1 flex items-center gap-1.5">
                <i className="ri-whatsapp-line text-sm" />
                Prévia da mensagem
              </p>
              <p className="text-xs text-[#3A7D52] leading-relaxed">{previewMessage}</p>
            </div>
          )}

          {/* Botão */}
          <button
            onClick={handleAgendar}
            disabled={!selected}
            className="w-full py-4 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:bg-[#1ebe5d] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
          >
            <i className="ri-whatsapp-line text-lg" />
            Abrir WhatsApp e Agendar
          </button>
          <p className="text-xs text-[#A89580] text-center mt-3">
            Você será redirecionado para o WhatsApp com a mensagem já preenchida.
          </p>
        </div>
      </div>
    </div>
  );
}
